import _ from "underscore";
import * as Sentry from "@sentry/browser";
import Log from "pages/common/lib/grabyo-log";
import ISOJson from "iso-639";
import Constants from "const/constants";
import { sha256 } from "util/hash-utils";

function Util() {
    /*******************************************
     * Public Methods
     *******************************************/

    this.isMobileUserAgent = function () {
        /**
         * Regex code from http://detectmobilebrowsers.com/
         * Regex updated: 9 September 2013
         */
        const a = navigator.userAgent || navigator.vendor || window.opera;
        // eslint-disable-next-line
        if (
            /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|android|ipad|playbook|silk|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
                a
            ) ||
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
                a.substr(0, 4)
            )
        ) {
            return true;
        } else {
            return false;
        }
    };

    this.supportsDownloadAttr = function () {
        /**
         * Uses the Modernizr approach
         */
        const a = document.createElement(`a`);
        return typeof a.download !== `undefined`;
    };

    /**
     * Utitlity method to parse a string and escape all html but still add in any links
     * This method splits the string into words and independently escapes or formats a URL into a link
     */
    this.escapeHtmlWithLinks = function (pString) {
        let formattedString = pString;
        if (formattedString !== null) {
            // eslint-disable-next-line
            let matchUrl =
                /(^|\s|\(|\)|\[|\]|\{|\}|,)((https?:\/\/)?[^\W_]([a-z\d-]*\.)+(ZW|ZM|ZA|YT|YE|XXX|WS|WIEN|WF|WANG|VU|VOYAGE|VN|VIAJES|VI|VG|VENTURES|VE|VC|VA|UZ|UY|US|UNO|UK|UG|UA|TZ|TW|TV|TT|TRAVEL|TRAINING|TR|TP|TODAY|TO|TN|TM|TL|TK|TJ|TIPS|TH|TG|TF|TEL|TECHNOLOGY|TD|TC|TATTOO|SZ|SYSTEMS|SY|SX|SV|SUPPORT|SU|ST|SR|SOLUTIONS|SOLAR|SO|SN|SM|SL|SK|SJ|SINGLES|SI|SHOES|SH|SG|SEXY|SE|SD|SC|SB|SA|RW|RUHR|RU|RS|RO|REPAIR|RECIPES|RE|QA|PY|PW|PT|PS|PRO|PR|POST|PN|PM|PLUMBING|PL|PK|PHOTOS|PHOTOGRAPHY|PH|PG|PF|PE|PA|ORG|ONL|OM|NZ|NU|NR|NP|NO|NL|NINJA|NI|NG|NF|NET|NE|NC|NAME|NA|MZ|MY|MX|MW|MV|MUSEUM|MU|MT|MS|MR|MQ|MP|MOBI|MO|MN|MM|ML|MK|MIL|MH|MG|MENU|ME|MD|MC|MANAGEMENT|MA|LY|LV|LU|LT|LS|LR|LK|LIMO|LIGHTING|LI|LC|LB|LAND|LA|KZ|KY|KW|KR|KP|KN|KM|KIWI|KITCHEN|KI|KH|KG|KE|KAUFEN|JP|JOBS|JO|JM|JE|IT|IS|IR|IQ|IO|INTERNATIONAL|INT|INSTITUTE|INFO|IN|IMMOBILIEN|IM|IL|IE|ID|HU|HT|HR|HOUSE|HOLIDAY|HOLDINGS|HN|HM|HK|GY|GW|GURU|GU|GT|GS|GRAPHICS|GR|GQ|GP|GOV|GN|GM|GLASS|GL|GI|GH|GG|GF|GE|GD|GB|GALLERY|GA|FR|FO|FM|FLORIST|FK|FJ|FI|FARM|EU|ET|ESTATE|ES|ER|EQUIPMENT|ENTERPRISES|EMAIL|EG|EE|EDUCATION|EDU|EC|DZ|DOMAINS|DO|DM|DK|DJ|DIRECTORY|DIAMONDS|DE|CZ|CY|CX|CW|CV|CU|CR|COOP|CONTRACTORS|CONSTRUCTION|COMPUTER|COMPANY|COM|COFFEE|CODES|CO|CN|CM|CLOTHING|CL|CK|CI|CH|CG|CF|CEO|CENTER|CD|CC|CAT|CAREERS|CAMP|CAMERA|CAB|CA|BZ|BY|BW|BV|BUZZ|BUILDERS|BT|BS|BR|BO|BN|BM|BJ|BIZ|BIKE|BI|BH|BG|BF|BERLIN|BE|BD|BB|BA|AZ|AX|AW|AU|AT|ASIA|AS|ARPA|AR|AQ|AO|AN|AM|AL|AI|AG|AF|AERO|AE|AD|ACADEMY|AC)([\/:\?][\/a-z\d%_\.~+\?,&=-]*)?)(?=\s|$|(\.\s)|(\.$)|(?=\W)(?!\.))/gi;
            const matchHttp = /http:\/\/http:\/\//gi;
            const matchHttps = /http:\/\/https:\/\//gi;

            const matchTwitterHandle = /(^|\.|\s)(@([a-z\d_]+))/gi;

            formattedString = formattedString
                .split(` `)
                .map(function (word) {
                    if (word.match(matchUrl) !== null) {
                        // eslint-disable-next-line
                        word = word.replace(
                            matchUrl,
                            `$1<a href="http://$2" target="_blank">$2</a>`
                        );
                        word = word.replace(matchHttp, `http://`);
                        word = word.replace(matchHttps, `https://`);
                    } else if (word.match(matchTwitterHandle) !== null) {
                        // eslint-disable-next-line
                        word = word.replace(
                            matchTwitterHandle,
                            `$1<a href="https://twitter.com/$3" target="_blank">$2</a>`
                        );
                    } else {
                        word = _.escape(word);
                    }
                    return word;
                })
                .join(` `);
        }

        return formattedString;
    };

    this.tokenizeShareDescription = function (
        pDescription,
        pSocialTaggingVars
    ) {
        pSocialTaggingVars = this.parseSocialTaggingVars(pSocialTaggingVars);

        let tokenizedDescription = pDescription;

        if (pSocialTaggingVars !== undefined && pSocialTaggingVars !== null) {
            try {
                for (let i = 0; i < pSocialTaggingVars.length; i++) {
                    if (pSocialTaggingVars[i].facebookName !== undefined) {
                        tokenizedDescription = tokenizedDescription.replace(
                            pSocialTaggingVars[i].facebookName,
                            `\${socialTaggingVar}`
                        );
                    } else if (
                        pSocialTaggingVars[i].twitterUsername !== undefined
                    ) {
                        tokenizedDescription = tokenizedDescription.replace(
                            `@${pSocialTaggingVars[i].twitterUsername}`,
                            `\${socialTaggingVar}`
                        );
                    }
                }
            } catch (error) {
                Log.error(`Could not tokenize description. Leaving as is.`);
            }
        }

        return tokenizedDescription;
    };

    this.tokenizeShareDescriptionForTwitter = function (
        pDescription,
        pSocialTaggingVars
    ) {
        pSocialTaggingVars = this.parseSocialTaggingVars(pSocialTaggingVars);

        let tokenizedDescription = pDescription;

        if (pSocialTaggingVars !== undefined && pSocialTaggingVars !== null) {
            try {
                for (let i = 0; i < pSocialTaggingVars.length; i++) {
                    if (pSocialTaggingVars[i].twitterUsername !== undefined) {
                        tokenizedDescription = tokenizedDescription.replace(
                            `\${socialTaggingVar}`,
                            `@${pSocialTaggingVars[i].twitterUsername}`
                        );
                    } else if (
                        pSocialTaggingVars[i].facebookName !== undefined
                    ) {
                        tokenizedDescription = tokenizedDescription.replace(
                            `\${socialTaggingVar}`,
                            pSocialTaggingVars[i].facebookName
                        );
                    }
                }
            } catch (error) {
                Log.error(`Could not tokenize description. Leaving as is.`);
            }
        }

        return tokenizedDescription;
    };

    this.tokenizeShareDescriptionWithLinks = function (
        pDescription,
        pSocialTaggingVars
    ) {
        pSocialTaggingVars = this.parseSocialTaggingVars(pSocialTaggingVars);

        let tokenizedDescription = pDescription;

        if (pSocialTaggingVars !== undefined && pSocialTaggingVars !== null) {
            try {
                for (let i = 0; i < pSocialTaggingVars.length; i++) {
                    let value = ``;
                    if (pSocialTaggingVars[i].facebookId !== undefined) {
                        value = `<a href="${pSocialTaggingVars[i].facebookUrl}" target="_blank">${pSocialTaggingVars[i].facebookName}</a>`;
                    } else if (
                        pSocialTaggingVars[i].twitterUsername !== undefined
                    ) {
                        value =
                            `<a href="${pSocialTaggingVars[i].twitterUrl}" target="_blank">` +
                            `@${pSocialTaggingVars[i].twitterUsername}</a>`;
                    }

                    tokenizedDescription = tokenizedDescription.replace(
                        `\${socialTaggingVar}`,
                        value
                    );
                }
            } catch (error) {
                Log.error(`Could not tokenize description. Leaving as is.`);
            }
        }

        return tokenizedDescription;
    };

    this.tokenizeShareDescriptionWithLinksForTwitter = function (
        pDescription,
        pSocialTaggingVars
    ) {
        pSocialTaggingVars = this.parseSocialTaggingVars(pSocialTaggingVars);

        let tokenizedDescription = pDescription;

        if (pSocialTaggingVars !== undefined && pSocialTaggingVars !== null) {
            try {
                for (let i = 0; i < pSocialTaggingVars.length; i++) {
                    let value = ``;
                    if (pSocialTaggingVars[i].twitterUsername !== undefined) {
                        value =
                            `<a href="${pSocialTaggingVars[i].twitterUrl}" target="_blank">` +
                            `@${pSocialTaggingVars[i].twitterUsername}</a>`;
                    } else if (
                        pSocialTaggingVars[i].facebookName !== undefined
                    ) {
                        value = pSocialTaggingVars[i].facebookName;
                    }

                    tokenizedDescription = tokenizedDescription.replace(
                        `\${socialTaggingVar}`,
                        value
                    );
                }
            } catch (error) {
                Log.error(`Could not tokenize description. Leaving as is.`);
            }
        }

        return tokenizedDescription;
    };

    this.parseSocialTaggingVars = function (pSocialTaggingVars) {
        if (_.isString(pSocialTaggingVars)) {
            try {
                pSocialTaggingVars = JSON.parse(pSocialTaggingVars);
            } catch (error) {
                Log.error(`Could not parse social tagging vars`);
            }
        }
        return pSocialTaggingVars;
    };

    this.tokenizeShareDescriptionWithLinksForFacebook = function (
        pDescription,
        pSocialTaggingVars
    ) {
        pSocialTaggingVars = this.parseSocialTaggingVars(pSocialTaggingVars);

        let tokenizedDescription = pDescription;

        if (pSocialTaggingVars !== undefined && pSocialTaggingVars !== null) {
            try {
                for (let i = 0; i < pSocialTaggingVars.length; i++) {
                    let value = ``;
                    if (pSocialTaggingVars[i].facebookId !== undefined) {
                        value = `<a href="${pSocialTaggingVars[i].facebookUrl}" target="_blank">${pSocialTaggingVars[i].facebookName}</a>`;
                    } else if (
                        pSocialTaggingVars[i].twitterUsername !== undefined
                    ) {
                        value = `@${pSocialTaggingVars[i].twitterUsername}`;
                    }

                    tokenizedDescription = tokenizedDescription.replace(
                        `\${socialTaggingVar}`,
                        value
                    );
                }
            } catch (error) {
                Log.error(`Could not tokenize description. Leaving as is.`);
            }
        }

        return tokenizedDescription;
    };

    this.tokenizeShareDescriptionWithLinksForCredentialType = function (
        pDescription,
        pSocialTaggingVars,
        pCredentialType
    ) {
        pSocialTaggingVars = this.parseSocialTaggingVars(pSocialTaggingVars);

        let tokenizedDescription = pDescription;

        switch (pCredentialType) {
            case `facebook`:
            case `facebook_page`:
                tokenizedDescription =
                    this.tokenizeShareDescriptionWithLinksForFacebook(
                        pDescription,
                        pSocialTaggingVars
                    );
                break;
            case `twitter`:
                tokenizedDescription =
                    this.tokenizeShareDescriptionWithLinksForTwitter(
                        pDescription,
                        pSocialTaggingVars
                    );
                break;
            default:
                tokenizedDescription = this.tokenizeShareDescriptionWithLinks(
                    pDescription,
                    pSocialTaggingVars
                );
                break;
        }

        return tokenizedDescription;
    };

    this.popUp = function (params) {
        const url = params.url;
        const target = params.target || `GrbySocialLoginPopUpWindow`;
        const onLoad = params.onLoad;
        const onClose = params.onClose;
        const defaultFeatures = {
            width: params.width === undefined ? 640 : params.width,
            height: params.height === undefined ? 480 : params.height,
            scrollbars:
                params.scrollbars === undefined ? `auto` : params.scrollbars
        };
        const windowFeatures = params?.features
            ? { ...defaultFeatures, ...params.features }
            : defaultFeatures;
        const featuresParams = Object.keys(windowFeatures).reduce(
            (str, key, index) => {
                const value = windowFeatures[key];
                return `${str}${index === 0 ? `` : `,`}${key}=${value}`;
            },
            ``
        );

        const newWindow = window.open(url, target, featuresParams);
        newWindow?.focus();

        if (onLoad !== undefined) {
            jQuery(newWindow.document).ready(function () {
                onLoad();
            });
        }

        if (onClose !== undefined) {
            const timer = setInterval(function () {
                if (newWindow.closed) {
                    clearInterval(timer);
                    onClose();
                }
            }, 1000);
        }
    };

    this.isBrowserIOS = function () {
        if (
            navigator.userAgent.toLowerCase().match(/(iphone|ipod|ipad)/) !==
            null
        ) {
            return true;
        } else {
            return false;
        }
    };

    this.isBrowserAndroid = function () {
        if (navigator.userAgent.toLowerCase().match(/(android)/) !== null) {
            return true;
        } else {
            return false;
        }
    };

    this.capitilizeFirstLetter = function (string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    /**
     * Util method to turns seconds to a time display format
     * i.e. 3661 --> 1:01:01
     */
    this.formatSecondsToTimeDisplay = function (seconds) {
        if (seconds) {
            seconds = Math.floor(seconds);
        } else {
            seconds = 0;
        }

        let timeString = ``;

        if (seconds >= 3600) {
            const hours = Math.floor(seconds / 3600);
            timeString += `${hours}:`;

            seconds = seconds % 3600;
        }

        if (seconds >= 60) {
            const mins = Math.floor(seconds / 60);
            timeString += mins >= 10 ? `${mins}:` : `0${mins}:`;

            seconds = seconds % 60;
        } else {
            timeString += `00:`;
        }

        timeString +=
            seconds >= 10 ? Math.floor(seconds) : `0${Math.floor(seconds)}`;

        return timeString;
    };

    /**
     * Util method to turns milliseconds to a time display format hh:mm:ss:sss
     * i.e. 3661001 --> 1:01:01:001
     */

    this.formatMillisecondsToTimeDisplay = function (milliseconds) {
        let timeString = ``;

        timeString += `${this.formatSecondsToTimeDisplay(
            milliseconds / 1000
        )}:`;

        milliseconds = milliseconds % 1000;
        timeString += `${Math.floor(milliseconds)}`.padStart(3, `0`);

        return timeString;
    };

    /**
     * Util method to turns seconds to a duration display format
     * i.e. 3661 --> 1h 1m 1s
     */
    this.formatSecondsToDurationDisplay = function (seconds) {
        if (seconds) {
            seconds = Math.floor(seconds);
        } else {
            seconds = 0;
        }

        let timeString = ``;

        if (seconds >= 3600) {
            const hours = Math.floor(seconds / 3600);
            timeString += `${hours}h `;

            seconds = seconds % 3600;
        }

        if (seconds >= 60) {
            const mins = Math.floor(seconds / 60);
            timeString += `${mins}m `;

            seconds = seconds % 60;
        }

        timeString += `${Math.floor(seconds)}s`;

        return timeString;
    };

    this.formatMillisecondsToDurationDisplay = function (milliseconds) {
        return this.formatSecondsToDurationDisplay(milliseconds / 1000);
    };

    /*\
		|*|
		|*|  :: cookies.js ::
		|*|
		|*|  A complete cookies reader/writer framework with full unicode support.
		|*|
		|*|  https://developer.mozilla.org/en-US/docs/DOM/document.cookie
		|*|
		|*|  This framework is released under the GNU Public License, version 3 or later.
		|*|  http://www.gnu.org/licenses/gpl-3.0-standalone.html
		|*|
		|*|  Syntaxes:
		|*|
		|*|  * docCookies.setItem(name, value[, end[, path[, domain[, secure]]]])
		|*|  * docCookies.getItem(name)
		|*|  * docCookies.removeItem(name[, path], domain)
		|*|  * docCookies.hasItem(name)
		|*|  * docCookies.keys()
		|*|
		\*/
    this.docCookies = {
        getItem: function (sKey) {
            // eslint-disable-next-line
            return (
                decodeURIComponent(
                    document.cookie.replace(
                        new RegExp(
                            `(?:(?:^|.*;)\\s*${encodeURIComponent(sKey).replace(
                                /[\-\.\+\*]/g,
                                `\\$&`
                            )}\\s*\\=\\s*([^;]*).*$)|^.*$`
                        ),
                        `$1`
                    )
                ) || null
            );
        },
        setItem: function (sKey, sValue, vEnd, sPath, sDomain, bSecure) {
            // eslint-disable-next-line
            if (
                !sKey ||
                /^(?:expires|max\-age|path|domain|secure)$/i.test(sKey)
            ) {
                return false;
            }
            let sExpires = ``;
            if (vEnd) {
                switch (vEnd.constructor) {
                    case Number:
                        sExpires =
                            vEnd === Infinity
                                ? `; expires=Fri, 31 Dec 9999 23:59:59 GMT`
                                : `; max-age=${vEnd}`;
                        break;
                    case String:
                        sExpires = `; expires=${vEnd}`;
                        break;
                    case Date:
                        sExpires = `; expires=${vEnd.toUTCString()}`;
                        break;
                }
            }
            document.cookie = `${encodeURIComponent(sKey)}=${encodeURIComponent(
                sValue
            )}${sExpires}${sDomain ? `; domain=${sDomain}` : ``}${
                sPath ? `; path=${sPath}` : ``
            }${bSecure ? `; secure` : ``}`;
            return true;
        },
        removeItem: function (sKey, sPath, sDomain) {
            if (!sKey || !this.hasItem(sKey)) {
                return false;
            }
            document.cookie = `${encodeURIComponent(
                sKey
            )}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${
                sDomain ? `; domain=${sDomain}` : ``
            }${sPath ? `; path=${sPath}` : ``}`;
            return true;
        },
        hasItem: function (sKey) {
            // eslint-disable-next-line
            return new RegExp(
                `(?:^|;\\s*)${encodeURIComponent(sKey).replace(
                    /[\-\.\+\*]/g,
                    `\\$&`
                )}\\s*\\=`
            ).test(document.cookie);
        },
        keys: function () {
            // eslint-disable-next-line
            const aKeys = document.cookie
                .replace(
                    // eslint-disable-next-line
                    /((?:^|\s*;)[^\=]+)(?=;|$)|^\s*|\s*(?:\=[^;]*)?(?:\1|$)/g,
                    ``
                )
                .split(/\s*(?:\=[^;]*)?;\s*/);
            for (let nIdx = 0; nIdx < aKeys.length; nIdx++) {
                aKeys[nIdx] = decodeURIComponent(aKeys[nIdx]);
            }
            return aKeys;
        }
    };

    this.generateUUIDv4 = function () {
        return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(
            /[xy]/g,
            function (c) {
                const r = (Math.random() * 16) | 0,
                    v = c === `x` ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            }
        );
    };

    this.generateGrabyoId = function (length) {
        let mask = `abcdefghijklmnopqrstuvwxyz`;
        mask += `ABCDEFGHIJKLMNOPQRSTUVWXYZ`;
        mask += `0123456789`;

        let result = ``;
        for (let i = length; i > 0; --i) {
            result += mask[Math.floor(Math.random() * mask.length)];
        }
        return result;
    };

    /**
     * [createLerpFunction creates a function that performs linear interpolation on a dataset]
     * @param  {[array]} points [ points with the following format: [[x, y], [x, y], [x, y], ..] ]
     * @return {[function]}     [linear, that functions will return the `y` value for any given `x` ]
     */
    this.createLerpFunction = function (points) {
        // This relies on linear interpolation, please check the wikipedia
        // to understand what it does and see the full formula.
        // https://en.wikipedia.org/wiki/Linear_interpolation

        const n = points.length - 1;

        if (points.length === 0) {
            return function () {
                return 0;
            };
        }

        if (points.length === 1) {
            return function () {
                return points[0][1];
            };
        }

        points = points.sort(function (a, b) {
            return a[0] - b[0];
        });
        const first = points[0];

        const leftExtrapolated = function (x) {
            const a = points[0],
                b = points[1];
            return a[1] + ((x - a[0]) * (b[1] - a[1])) / (b[0] - a[0]);
        };

        const interpolated = function (x, a, b) {
            return a[1] + ((x - a[0]) * (b[1] - a[1])) / (b[0] - a[0]);
        };

        const rightExtrapolated = function (x) {
            const a = points[n - 1],
                b = points[n];
            return b[1] + ((x - b[0]) * (b[1] - a[1])) / (b[0] - a[0]);
        };

        return function (x) {
            let i;

            if (x <= first[0]) {
                return leftExtrapolated(x);
            }
            for (i = 0; i < n; i += 1) {
                if (x > points[i][0] && x <= points[i + 1][0]) {
                    return interpolated(x, points[i], points[i + 1]);
                }
            }
            return rightExtrapolated(x);
        };
    };

    /**
     * Util method to turns seconds to a time display format
     * i.e. 3661 --> 1:01:01
     */
    this.formatSecondsToTimeDisplay = function (seconds) {
        if (seconds) {
            seconds = Math.floor(seconds);
        } else {
            seconds = 0;
        }

        let timeString = ``;

        if (seconds >= 3600) {
            const hours = Math.floor(seconds / 3600);
            timeString += `${hours}:`;

            seconds = seconds % 3600;
        }

        if (seconds >= 60) {
            const mins = Math.floor(seconds / 60);
            timeString += mins >= 10 ? `${mins}:` : `0${mins}:`;

            seconds = seconds % 60;
        } else {
            timeString += `00:`;
        }

        timeString +=
            seconds >= 10 ? Math.floor(seconds) : `0${Math.floor(seconds)}`;

        return timeString;
    };

    /**
     * Util method to turns seconds to a duration display format
     * i.e. 3661 --> 1h 1m 1s
     */
    this.formatSecondsToDurationDisplay = function (seconds = 0) {
        let timeString = ``;

        if (seconds >= 3600) {
            const hours = Math.floor(seconds / 3600);
            timeString += `${hours}h `;

            seconds = seconds % 3600;
        }

        if (seconds >= 60) {
            const mins = Math.floor(seconds / 60);
            timeString += `${mins}m `;

            seconds = seconds % 60;
        }

        //timeString += Math.floor(seconds) + "s";
        // Format to deciseconds instead of seconds
        timeString += `${Math.round(seconds * 10) / 10}s`;

        return timeString;
    };

    this.isUrlValid = function (pUrl) {
        // urlRegex from - https://gist.github.com/dperini/729294 - @diegoperini version from https://mathiasbynens.be/demo/url-regex
        const urlRegex =
            /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/i;
        return urlRegex.test(pUrl);
    };

    this.isFacebookUrl = function (pUrl) {
        // eslint-disable-next-line
        const urlRegex =
            /^(?:https?:\/\/)?(?:www\.)?facebook\.com\/([a-zA-Z0-9\.]+)\/?(?:.*)$/i;
        return urlRegex.test(pUrl);
    };

    this.isFacebookUid = function (uid) {
        // The "double equal is used on purpose here".
        // using parseInt on a non numeric value will return `NaN` in most cases
        // but if the query is `123abc`, then parseInt will return `123`.
        // So we want to parseInt and check if the returned value is the
        // same as the input string.

        // eslint-disable-next-line
        const isPositiveInteger = uid == parseInt(uid, 10) ? true : false;
        const isMoreThan8Chars =
            _.isString(uid) && uid.length >= 6 ? true : false;

        return isPositiveInteger && isMoreThan8Chars ? true : false;
    };

    this.getAspectRatioFraction = function (aspectRatio) {
        aspectRatio = _.isString(aspectRatio) ? aspectRatio : `16:9`;
        const aspectRatioArr = aspectRatio.split(`:`).map(function (n) {
            return parseInt(n, 10);
        });
        return aspectRatioArr[0] / aspectRatioArr[1];
    };

    this.getSmallestAspectRatio = function (a, b) {
        const aAspectRatioFraction = this.getAspectRatioFraction(a);
        const bAspectRatioFraction = this.getAspectRatioFraction(b);
        if (
            Math.min(aAspectRatioFraction, bAspectRatioFraction) ===
            aAspectRatioFraction
        ) {
            return a;
        } else {
            return b;
        }
    };

    this.getBiggestAspectRatio = function (a, b) {
        const aAspectRatioFraction = this.getAspectRatioFraction(a);
        const bAspectRatioFraction = this.getAspectRatioFraction(b);
        if (
            Math.max(aAspectRatioFraction, bAspectRatioFraction) ===
            aAspectRatioFraction
        ) {
            return a;
        } else {
            return b;
        }
    };

    this.log = function (level, message) {
        switch (level) {
            case `info`:
            case `warning`:
            case `error`:
                break;
            default:
                console.error(
                    `Incorrect level, \`log\` accepts \`info\`, \`warning\` and \`error\``
                );
                return;
        }

        if (window.Sentry) {
            Sentry.captureMessage(message, {
                level
            });
        } else {
            level = level !== `warning` ? level : `warn`;
            console[level](message);
        }
    };

    this.clipboardFallbackMessage = function (action) {
        let actionMsg = ``;
        const actionKey = action === `cut` ? `X` : `C`;
        if (/iPhone|iPad/i.test(navigator.userAgent)) {
            actionMsg = `No support :(`;
        } else if (/Mac/i.test(navigator.userAgent)) {
            actionMsg = `Press ⌘-${actionKey} to ${action}`;
        } else {
            actionMsg = `Press Ctrl-${actionKey} to ${action}`;
        }
        return actionMsg;
    };

    this.KEY = {
        ALT: 18,
        RETURN: 13,
        BACKSPACE: 8,
        CONTROL: 17,
        DELETE: 46,
        ESCAPE: 27,
        SPACEBAR: 32,
        LEFTARR: 37,
        UPARR: 38,
        RIGHTARR: 39,
        DOWNARR: 40,
        PLUS: 187,
        EQ: 61, // Firefox only
        MINUS: 189,
        DASH: 173,
        LT: 188,
        BT: 190,
        QUESTION_MARK: 191,
        GRAVE_ACCENT: 192,
        GRAVE_ACCENT_WINDOWS: 223,
        TAB: 9,
        ZERO: 48,
        0: 48,
        ONE: 49,
        1: 49,
        TWO: 50,
        2: 50,
        THREE: 51,
        3: 51,
        FOUR: 52,
        4: 52,
        FIVE: 53,
        5: 53,
        SIX: 54,
        6: 54,
        SEVEN: 55,
        7: 55,
        EIGHT: 56,
        8: 56,
        NINE: 57,
        9: 57,
        A: 65,
        C: 67,
        F: 70,
        I: 73,
        J: 74,
        K: 75,
        L: 76,
        M: 77,
        O: 79,
        P: 80,
        Q: 81,
        T: 84,
        W: 87,
        Y: 89,
        Z: 90
    };

    this.convertSocialTaggingVars = function (
        socialVar,
        socialMentionToHide = `twitter`
    ) {
        const mention = {
            icon: `icon-16 icon-person`,
            // eslint-disable-next-line
            is_verified: true,
            type: `contact`,
            display: true
        };

        if (socialMentionToHide === `facebook`) {
            mention.platform = `twitter`;
            mention.value = `＠${socialVar.twitterUsername}`;
            mention.id = socialVar.twitterId;
            mention.name = socialVar.twitterName;
            mention.username = `＠${socialVar.twitterUsername}`;
            mention.avatar = socialVar.twitterProfileImageUrl;
            mention.url = socialVar.twitterUrl;
        }

        if (socialMentionToHide === `twitter`) {
            mention.platform = `facebook`;
            mention.value = socialVar.facebookName;
            mention.id = socialVar.facebookId;
            mention.name = socialVar.facebookName;
            mention.username = socialVar.facebookUsername;
            mention.avatar = `https://graph.facebook.com/v2.6/${socialVar.facebookId}/picture`;
            mention.url = socialVar.facebookUrl;
        }

        return mention;
    };
    this.convertSocialTaggingVarToMentionsInputObj = function (
        socialVar,
        socialMentionToHide
    ) {
        const isPairedMention =
            socialVar.facebookId !== undefined &&
            socialVar.twitterId !== undefined;

        /**
         * Setup the base 'Mention Obj' for mentionsInput plugin
         */
        const mention = {
            icon: `icon-16 icon-person`,
            // eslint-disable-next-line
            is_verified: true,
            type: `contact`,
            display: true
        };

        if (isPairedMention) {
            /**
             * Setup the structure to be FB first, then TW as an 'extra'
             * i.e.
             * {
             *      id: facebookId,
             *      name: facebookName,
             *      ...
             *      extras: [
             *          {
             *              id: twitterId,
             *              name: twitterName,
             *              ...
             *          }
             *      ]
             * }
             */
            const twitterMention = _.clone(mention);

            twitterMention.platform = `twitter`;
            twitterMention.value = `＠${socialVar.twitterUsername}`;
            twitterMention.id = socialVar.twitterId;
            twitterMention.name = socialVar.twitterName;
            twitterMention.username = `＠${socialVar.twitterUsername}`;
            twitterMention.avatar = socialVar.twitterProfileImageUrl;
            twitterMention.url = socialVar.twitterUrl;
            twitterMention.display = true;

            if (
                _.isString(socialMentionToHide) &&
                socialMentionToHide === `twitter`
            ) {
                twitterMention.display = false;
            }

            mention.extras = [twitterMention];

            mention.platform = `facebook`;
            mention.value = socialVar.facebookName;
            mention.id = socialVar.facebookId;
            mention.name = socialVar.facebookName;
            mention.username = socialVar.facebookUsername;
            mention.avatar = `https://graph.facebook.com/v2.6/${socialVar.facebookId}/picture`;
            mention.url = socialVar.facebookUrl;

            if (
                _.isString(socialMentionToHide) &&
                socialMentionToHide === `facebook`
            ) {
                mention.display = false;
            }
        } else {
            if (socialVar.facebookId !== undefined) {
                mention.platform = `facebook`;
            } else if (socialVar.twitterId !== undefined) {
                mention.platform = `twitter`;
            }

            if (mention.platform === `facebook`) {
                /**
                 * Setup the structure to be FB
                 * i.e.
                 * {
                 *      id: facebookId,
                 *      name: facebookName,
                 *      ...
                 * }
                 */
                mention.value = socialVar.facebookName;
                mention.id = socialVar.facebookId;
                mention.name = socialVar.facebookName;
                mention.username = socialVar.facebookUsername;
                mention.avatar = `https://graph.facebook.com/v2.6/${socialVar.facebookId}/picture`;
                mention.url = socialVar.facebookUrl;

                if (
                    _.isString(socialMentionToHide) &&
                    socialMentionToHide === `facebook`
                ) {
                    mention.display = false;
                }
            } else if (mention.platform === `twitter`) {
                /**
                 * Setup the structure to be TW
                 * i.e.
                 * {
                 *      id: twitterId,
                 *      name: twitterName,
                 *      ...
                 * }
                 */
                mention.value = `＠${socialVar.twitterUsername}`;
                mention.id = socialVar.twitterId;
                mention.name = socialVar.twitterName;
                mention.username = `＠${socialVar.twitterUsername}`;
                mention.avatar = socialVar.twitterProfileImageUrl;
                mention.url = socialVar.twitterUrl;

                if (
                    _.isString(socialMentionToHide) &&
                    socialMentionToHide === `twitter`
                ) {
                    mention.display = false;
                }
            }
        }

        if (
            mention.display ||
            (mention.extras &&
                _.some(mention.extras, function (men) {
                    return men.display === true;
                }))
        ) {
            mention.displayTooltip = true;
        } else {
            mention.displayTooltip = false;
        }

        return mention;
    };

    this.findByProp = (array, prop, propValue) => {
        if (!_.isArray(array)) {
            throw new TypeError(
                `findByProp() - array argument is not type Array`
            );
        }
        if (!_.isString(prop)) {
            throw new TypeError(
                `findByProp() - prop argument is not type String`
            );
        }
        if (_.isUndefined(propValue)) {
            throw new TypeError(`findByProp() - no comparison defined`);
        }

        const result = array.filter(
            (obj) => _.isObject(obj) && obj[prop] === propValue
        );

        if (!_.isEmpty(result)) {
            return result;
        }
    };

    this.findOneByProp = (array, prop, propValue) => {
        if (!_.isArray(array)) {
            throw new TypeError(
                `findByProp() - array argument is not type Array`
            );
        }
        if (!_.isString(prop)) {
            throw new TypeError(
                `findByProp() - prop argument is not type String`
            );
        }
        if (_.isUndefined(propValue)) {
            throw new TypeError(`findByProp() - no comparison defined`);
        }
        for (const i in array) {
            const obj = array[i];
            if (_.isObject(obj) && obj[prop] === propValue) {
                return obj;
            }
        }
    };

    this.getArrayTruthyPercentage = (array) => {
        if (!_.isArray(array)) {
            return;
        }
        if (!array.length) {
            return 0;
        }
        return (array.filter((val) => val).length / array.length) * 100;
    };

    this.switchStream = function () {
        // When changing stream we don't want to keep those two query params
        // Because we automatically jump to live
        const start = parseInt(App.Utils.programStartTime, 10);
        const end = parseInt(App.Utils.programEndTime, 10);
        if (
            start === window.data.programStartTime &&
            end === window.data.programEndTime
        ) {
            delete App.Utils.params.programStartTime;
            delete App.Utils.params.programEndTime;
            delete window.data.programStartTime;
            delete window.data.programEndTime;
        }
    };

    this.accountToApiFormat = async function (values) {
        if (
            _.isString(values.currentPassword) &&
            values.currentPassword.length > 0
        ) {
            values.currentPassword = await sha256(values.currentPassword);
        } else {
            delete values.currentPassword;
        }

        delete values.passwordConfirm;
        if (_.isString(values.password) && values.password.length > 0) {
            values.password = await sha256(values.password);
        } else {
            delete values.password;
        }

        if (
            !_.isString(values.uploadedProfileImage) ||
            values.uploadedProfileImage.length === 0
        ) {
            delete values.uploadedProfileImage;
        }

        return values;
    };

    this.getErrorMessageFromResponse = (xhr) => {
        return xhr.responseJSON && xhr.responseJSON.error
            ? xhr.responseJSON.error.errorMessage
            : null;
    };

    this.getLanguageNameByISO = (code) => {
        const ISO1 = ISOJson.iso_639_1[code];
        const ISO2 = ISOJson.iso_639_2[code];
        if (_.isObject(ISO1)) {
            return ISO1.name;
        }
        if (_.isObject(ISO2)) {
            return ISO2.en[0];
        }
    };

    /**
     * @summary - Find the maximum value of a given property in an array of objects
     * @function
     *
     * @param {Array} arr - an array of objects
     * @param {String} key - the property to check
     *
     * @example
     * getMaxByProp([{ num: 1 }, { num: 2 }, { num: 3, foo: 500 }], `num`) // 3
     */

    this.getMaxByProp = (arr, key) => {
        let max = 0;
        arr.forEach((item) => (max = item[key] > max ? item[key] : max));
        return max;
    };

    /**
     * @summary - Convert a map of objects to an array. Adds the key to each object unlike `Array.from()`
     * @function
     *
     * @param {Map} map - A map of objects
     * @param {String} [keyname=id] - The property the item's key will be assigned to
     *
     * @returns {Array} - An array of map's values with their respective keys as the property [keyname]
     * @example
     * const map = new Map([
     *	  [`foo`, { "testprop": 1 }],
     *	  [`bar`, { "testprop": 2 }]
     * ];
     * mapToKeyedArray(map, `key`);
     * // [{ key: `foo`, value: { testprop: 1 } }, { key: `bar`, value: { testprop: 2 } }]
     */
    this.mapToKeyedArray = (map, keyname = `id`) =>
        Array.from(map).map(([key, val]) => ({ [keyname]: key, value: val }));

    /**
     * @summary - Convert a map of objects to an array
     * @function
     *
     * @param {Map} map - A map of objects
     *
     * @returns {Array} - An array of map's values
     * @example
     * const map = new Map([
     *	  [`foo`, { "testprop": 1 }],
     *	  [`bar`, { "testprop": 2 }]
     * ];
     * mapToArray(map, `key`);
     * // [{ testprop: 1 }, { testprop: 2 }]
     */
    this.mapToArray = (map) => Array.from(map).map(([, val]) => val);

    /**
     * @summary Retuns grab type.
     * @param {GrabModel} grabType Type of GrabModel instance.
     * @returns {String} grab type as string.
     * @example
     * getGrabType(grab.get('type'))
     * // if type="clip" returns "vod"
     */
    this.getGrabType = (grabType) => {
        switch (grabType) {
            case Constants.MEDIA_TYPE.CLIP:
                return Constants.MEDIA_TYPE.VOD;
            case Constants.MEDIA_TYPE.GIF:
                return Constants.MEDIA_TYPE.GIF;
            case Constants.MEDIA_TYPE.IMAGE:
                return Constants.MEDIA_TYPE.IMAGE;
        }
    };

    /**
     * @summary Returns browser locale depending on the browser e.g. chrome navigator returns an array, firefox returns a string
     * @returns {String} locale code e.g. en-GB
     */
    this.getBrowserLocale = () => {
        return navigator.languages[0] || navigator.language;
    };

    /**
     * @summary Returns an updated style and order for our tooltip, specifically for compatibility with our existing backbone modal
     * @returns {Object} popper.js modifiers
     */
    this.tooltipPopperOptions = (options) => {
        const popperOptions = {
            modifiers: {
                ...options?.modifiers,
                addZIndex: {
                    enabled: true,
                    order: 810, // react-tippy uses popper.js, this number is the index used to define the order of executed elements
                    fn: (data) => ({
                        ...data,
                        styles: {
                            ...data.styles,
                            maxWidth: options?.maxWidth
                                ? `${options?.maxWidth}px`
                                : undefined,
                            zIndex: 100000001 // our current bootstrap modal has a z-index override of 100000000, hence the extreme number
                        }
                    })
                }
            }
        };
        return popperOptions;
    };
}

Util.prototype = {
    toString: function () {
        return `[Grby.Lib.Util class]`;
    }
};
export default new Util();

/**
 * @summary - Create a deep clone of an object
 * @param {Object} obj - A serializable object
 *
 * @returns {Object} - A clone of the object
 */
export const deepClone = (obj = {}) => JSON.parse(JSON.stringify(obj));
